import './TestApplicationForm.css';
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { loadPaymentWidget } from '@tosspayments/payment-widget-sdk';
import { navigationBarState } from '../../states/NavigationBar';
import { LoggedInState } from '../../states/LoggedIn';
import toast from 'react-hot-toast';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// 토스페이먼츠 테스트 키 (실제 배포 시 환경변수로 교체)
const TOSS_CLIENT_KEY = 'test_gck_docs_Ovk5rk1EwkEbP0W43n07xlzm';

function TestApplicationForm() {
    const navigate = useNavigate();
    const location = useLocation();
    const setNavigationBarState = useSetRecoilState(navigationBarState);
    const loggedIn = useRecoilValue(LoggedInState);

    const test = location.state?.test;

    // Form fields
    const [englishName, setEnglishName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [originalPhoneNumber, setOriginalPhoneNumber] = useState('');
    const [verificationCode, setVerificationCode] = useState('');

    // States
    const [loading, setLoading] = useState(true);
    const [phoneVerified, setPhoneVerified] = useState(true);
    const [termsAgreed, setTermsAgreed] = useState(false);
    const [verificationSent, setVerificationSent] = useState(false);
    const [timer, setTimer] = useState(0);
    const [resendTimer, setResendTimer] = useState(0);
    const [sendingCode, setSendingCode] = useState(false);
    const [verifyingCode, setVerifyingCode] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Refs
    const codeRef = useRef(null);
    const paymentWidgetRef = useRef(null);
    const paymentMethodsRef = useRef(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        setNavigationBarState({
            title: '시험 신청',
            'left-item': { type: 'back' },
        });

        if (!test) {
            toast.error('시험 정보를 찾을 수 없습니다.');
            navigate(-1);
            return;
        }

        fetchUserProfile();
    }, [setNavigationBarState, test, navigate]);

    // Initialize payment widget for paid tests
    useEffect(() => {
        if (!test || !loggedIn?.user) return;

        const finalPrice = test.sale_price && test.sale_price < test.price
            ? test.sale_price
            : test.price;

        if (finalPrice <= 0) return;

        const initPaymentWidget = async () => {
            try {
                // Use user ID as customerKey
                const customerKey = `user_${loggedIn.user.id}`;
                const paymentWidget = await loadPaymentWidget(TOSS_CLIENT_KEY, customerKey);
                paymentWidgetRef.current = paymentWidget;

                // Render payment methods
                const paymentMethods = paymentWidget.renderPaymentMethods(
                    '#payment-widget',
                    { value: finalPrice },
                    { variantKey: 'DEFAULT' }
                );
                paymentMethodsRef.current = paymentMethods;

                // Render agreement
                paymentWidget.renderAgreement('#agreement-widget', { variantKey: 'AGREEMENT' });
            } catch (error) {
                console.error('Failed to initialize payment widget:', error);
            }
        };

        initPaymentWidget();
    }, [test, loggedIn?.user]);

    // Timer for verification code
    useEffect(() => {
        let interval = null;
        if (timer > 0) {
            interval = setInterval(() => {
                setTimer(t => t - 1);
            }, 1000);
        } else if (timer === 0 && verificationSent && !phoneVerified) {
            setVerificationSent(false);
        }
        return () => clearInterval(interval);
    }, [timer, verificationSent, phoneVerified]);

    // Timer for resend cooldown
    useEffect(() => {
        let interval = null;
        if (resendTimer > 0) {
            interval = setInterval(() => {
                setResendTimer(t => t - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [resendTimer]);

    const fetchUserProfile = async () => {
        try {
            const response = await axios.get('/users/me/');
            const user = response.data;
            setEnglishName(user.english_name || '');
            const formattedPhone = formatPhoneNumber(user.phone_number || '');
            setPhoneNumber(formattedPhone);
            setOriginalPhoneNumber(formattedPhone);
            setPhoneVerified(true);
        } catch (error) {
            console.error('Failed to fetch user profile:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatTimer = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const formatPhoneNumber = (value) => {
        const numbers = value.replace(/[^\d]/g, '');
        if (numbers.length <= 3) return numbers;
        if (numbers.length <= 7) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
        return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
    };

    const handlePhoneChange = (e) => {
        const formatted = formatPhoneNumber(e.target.value);
        setPhoneNumber(formatted);

        // Check if phone number changed from original
        if (formatted !== originalPhoneNumber) {
            setPhoneVerified(false);
            setVerificationSent(false);
        } else {
            setPhoneVerified(true);
        }
    };

    const sendVerificationCode = async () => {
        const phone = phoneNumber.replace(/[^\d]/g, '');
        if (phone.length < 10) {
            toast.error('올바른 휴대폰 번호를 입력해주세요.');
            return;
        }

        if (resendTimer > 0) {
            toast.error(`${resendTimer}초 후에 다시 시도해주세요.`);
            return;
        }

        setSendingCode(true);
        try {
            const response = await axios.post('/users/send-verification/', {
                phone_number: phone
            });
            if (response.data.success) {
                toast.success('인증번호가 발송되었습니다.');
                setVerificationSent(true);
                setTimer(180);
                setResendTimer(60);
                setVerificationCode('');
                codeRef.current?.focus();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || '인증번호 발송에 실패했습니다.');
        } finally {
            setSendingCode(false);
        }
    };

    const verifyCode = async () => {
        if (verificationCode.length !== 6) {
            toast.error('6자리 인증번호를 입력해주세요.');
            return;
        }

        const phone = phoneNumber.replace(/[^\d]/g, '');
        setVerifyingCode(true);
        try {
            const response = await axios.post('/users/verify-code/', {
                phone_number: phone,
                verification_code: verificationCode
            });
            if (response.data.success) {
                toast.success('휴대폰 인증이 완료되었습니다.');
                setPhoneVerified(true);
                setTimer(0);
                setOriginalPhoneNumber(phoneNumber);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || '인증에 실패했습니다.');
        } finally {
            setVerifyingCode(false);
        }
    };

    const formatTestTime = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}/${month}/${day} ${hours}:${minutes}`;
    };

    const finalPrice = test?.sale_price && test.sale_price < test?.price
        ? test.sale_price
        : (test?.price || 0);

    // For paid tests, agreement is handled by payment widget
    const isFormValid =
        englishName.trim().length > 0 &&
        phoneNumber.replace(/[^\d]/g, '').length >= 10 &&
        phoneVerified &&
        (finalPrice > 0 || termsAgreed);

    const handleSubmit = async () => {
        if (!isFormValid) return;

        const finalPrice = test.sale_price && test.sale_price < test.price
            ? test.sale_price
            : test.price;

        // Free test - directly submit
        if (finalPrice <= 0) {
            setSubmitting(true);
            try {
                const response = await axios.post('/tests/store/apply/', {
                    test_id: test.id,
                    english_name: englishName.trim(),
                    phone_number: phoneNumber.replace(/[^\d]/g, '')
                });

                if (response.data.success) {
                    toast.success('시험 신청이 완료되었습니다.');
                    navigate(-1);
                } else {
                    toast.error(response.data.message || '신청에 실패했습니다.');
                }
            } catch (error) {
                toast.error(error.response?.data?.message || '신청에 실패했습니다.');
            } finally {
                setSubmitting(false);
            }
            return;
        }

        // Paid test - use payment widget
        if (!paymentWidgetRef.current) {
            toast.error('결제 모듈을 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
            return;
        }

        setSubmitting(true);
        try {
            // Generate unique order ID
            const orderId = `TEST_${test.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

            // Store application data in sessionStorage for later use
            sessionStorage.setItem('pendingTestApplication', JSON.stringify({
                test_id: test.id,
                english_name: englishName.trim(),
                phone_number: phoneNumber.replace(/[^\d]/g, ''),
                order_id: orderId
            }));

            // Request payment
            await paymentWidgetRef.current.requestPayment({
                orderId: orderId,
                orderName: test.title,
                customerName: loggedIn.user.nickname || loggedIn.user.username,
                customerEmail: loggedIn.user.email || undefined,
                successUrl: `${window.location.origin}/test-store/payment/success`,
                failUrl: `${window.location.origin}/test-store/payment/fail`,
            });
        } catch (error) {
            // User cancelled or error occurred
            if (error.code === 'USER_CANCEL') {
                toast.error('결제가 취소되었습니다.');
            } else {
                toast.error(error.message || '결제 요청 중 오류가 발생했습니다.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="application-form-page">
                <div className="application-form-container">
                    <div className="application-form-loading">로딩중...</div>
                </div>
            </div>
        );
    }

    if (!test) return null;

    const isAlwaysAvailable = !test.start_time && !test.end_time;

    return (
        <div className="application-form-page">
            <div className="application-form-container">
                {/* Test Info Section */}
                <div className="application-form-test-info">
                    <div className="application-form-test-title">{test.title}</div>
                    {test.description && (
                        <div className="application-form-test-desc">{test.description}</div>
                    )}
                    <div className="application-form-test-meta">
                        {test.number_of_questions}문항 · {Math.round(test.time_limit / 60)}분 · {test.total_score}점
                    </div>
                    <div className="application-form-test-price">
                        {test.price > 0 ? (
                            test.sale_price && test.sale_price < test.price ? (
                                <>
                                    <span className="application-form-discount-badge">
                                        {Math.round((1 - test.sale_price / test.price) * 100)}%
                                    </span>
                                    <span className="application-form-original-price">
                                        {test.price.toLocaleString()}원
                                    </span>
                                    <span className="application-form-sale-price">
                                        {test.sale_price.toLocaleString()}원
                                    </span>
                                </>
                            ) : (
                                <span className="application-form-sale-price">
                                    {test.price.toLocaleString()}원
                                </span>
                            )
                        ) : (
                            <span className="application-form-free-badge">무료</span>
                        )}
                    </div>
                    {!isAlwaysAvailable && (
                        <div className="application-form-test-schedule">
                            <div className="application-form-test-schedule-label">응시 가능 시간</div>
                            <div className="application-form-test-schedule-value">
                                {formatTestTime(test.start_time)} ~ {formatTestTime(test.end_time)}
                            </div>
                        </div>
                    )}
                </div>

                {/* Form Section */}
                <div className="application-form-section">
                    <div className="application-form-section-title">신청 정보</div>

                    {/* English Name */}
                    <div className="application-form-input-group">
                        <label className="application-form-input-label">영문 이름</label>
                        <div className="application-form-input-wrapper">
                            <input
                                type="text"
                                className="application-form-input"
                                placeholder="영문 이름 입력 (예: Hong Gildong)"
                                value={englishName}
                                onChange={(e) => setEnglishName(e.target.value)}
                                maxLength={100}
                            />
                        </div>
                        <div className="application-form-input-hint">성적표 및 인증서에 표기될 영문 이름입니다.</div>
                    </div>

                    {/* Phone Number */}
                    <div className="application-form-input-group">
                        <label className="application-form-input-label">
                            휴대폰번호
                            {phoneVerified && (
                                <span className="application-form-verified-badge">
                                    <CheckCircleIcon style={{ fontSize: 14 }} />
                                    인증완료
                                </span>
                            )}
                        </label>
                        <div className="application-form-input-wrapper">
                            <input
                                type="tel"
                                className={`application-form-input with-button ${phoneVerified ? 'success' : ''}`}
                                placeholder="010-0000-0000"
                                value={phoneNumber}
                                onChange={handlePhoneChange}
                                maxLength={13}
                            />
                            {!phoneVerified && (
                                <button
                                    className={`application-form-input-button ${verificationSent ? 'outlined' : ''}`}
                                    onClick={sendVerificationCode}
                                    disabled={phoneNumber.replace(/[^\d]/g, '').length < 10 || sendingCode || resendTimer > 0}
                                >
                                    {sendingCode ? '발송중...' :
                                     resendTimer > 0 ? `${resendTimer}초` :
                                     (verificationSent ? '재발송' : '인증요청')}
                                </button>
                            )}
                        </div>
                        <div className="application-form-input-hint">시험 응시에 관련된 정보를 수신할 번호입니다.</div>
                    </div>

                    {/* Verification Code */}
                    {verificationSent && !phoneVerified && (
                        <div className="application-form-input-group">
                            <label className="application-form-input-label">인증번호</label>
                            <div className="application-form-verification-row">
                                <div className="application-form-input-wrapper application-form-verification-input">
                                    <input
                                        ref={codeRef}
                                        type="text"
                                        className="application-form-input"
                                        placeholder="6자리 숫자 입력"
                                        value={verificationCode}
                                        onChange={(e) => setVerificationCode(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
                                        maxLength={6}
                                    />
                                </div>
                                <div className="application-form-verification-timer">{formatTimer(timer)}</div>
                                <button
                                    className="application-form-verification-button"
                                    onClick={verifyCode}
                                    disabled={verificationCode.length !== 6 || verifyingCode}
                                >
                                    {verifyingCode ? '...' : '확인'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Payment Widget (for paid tests) */}
                {test.price > 0 && (
                    <div className="application-form-section">
                        <div className="application-form-section-title">결제 수단</div>
                        <div id="payment-widget" className="application-form-payment-widget"></div>
                        <div id="agreement-widget" className="application-form-agreement-widget"></div>
                    </div>
                )}

                {/* Terms (for free tests only) */}
                {test.price <= 0 && (
                    <div className="application-form-terms">
                        <label className="application-form-terms-checkbox">
                            <input
                                type="checkbox"
                                checked={termsAgreed}
                                onChange={(e) => setTermsAgreed(e.target.checked)}
                            />
                            <span className="application-form-terms-text">
                                <a href="/test-terms" target="_blank" rel="noopener noreferrer">시험 응시 및 결제 약관</a>에 동의합니다.
                            </span>
                        </label>
                    </div>
                )}

                {/* Submit Button */}
                <button
                    className="application-form-submit-button"
                    onClick={handleSubmit}
                    disabled={!isFormValid || submitting}
                >
                    {submitting ? (
                        <>
                            <span className="application-form-loading-spinner"></span>
                            결제 중...
                        </>
                    ) : '결제하기'}
                </button>
            </div>
        </div>
    );
}

export default TestApplicationForm;
